import { isPlatformServer } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { APP_ID, inject, Injectable, isDevMode, PLATFORM_ID } from '@angular/core';
import {
  addBreadcrumb,
  BrowserOptions,
  browserTracingIntegration,
  captureException,
  init,
  replayIntegration,
  setTag,
} from '@sentry/angular';
import { filter, take } from 'rxjs';
import { BRAND_CONFIG } from 'brand';
import { FeatureFlagService } from 'feature-flag';
import { BrowserService } from 'utils';
import { CustomError } from './custom-error.type';
import { release } from './release';

@Injectable({
  providedIn: 'root',
})
export class SentryService {
  private brand = inject(BRAND_CONFIG).brand;
  private featureFlags = inject(FeatureFlagService);
  private platformId = inject(PLATFORM_ID);
  private browserService = inject(BrowserService);
  private appId = inject(APP_ID, { optional: true });

  constructor() {
    if (isPlatformServer(this.platformId)) return;
    if (this.getEnvironment() !== 'production') return;
    this.featureFlags.ready.pipe(filter(Boolean), take(1)).subscribe(() => {
      if (!this.featureFlags.isOn('sentry-settings')) return;
      this.initSentry();
    });
  }

  private initSentry() {
    const environment = this.getEnvironment();
    const { dsn, sampleRate, tracesSampleRate, replaysSessionSampleRate, replaysOnErrorSampleRate } =
      this.featureFlags.getFeatureValue<BrowserOptions>('sentry-settings', {});
    init({
      environment,
      dsn,
      sampleRate,
      tracesSampleRate,
      replaysSessionSampleRate,
      replaysOnErrorSampleRate,
      integrations: [browserTracingIntegration(), replayIntegration()],
      tracePropagationTargets: [
        // We can add tracePropagationTargets back when the microservices support it
        // new RegExp(`^${this.config.newMicroServiceEndpoint.replace(/\./g, '\\.').replace(/\//g, '\\/')}`),
      ],
      release,
      denyUrls: [
        // Sentry recommendations: https://docs.sentry.io/platforms/javascript/legacy-sdk/tips/
        // Google Adsense
        /pagead\/js/i,
        // Facebook flakiness
        /graph\.facebook\.com/i,
        // Facebook blocked
        /connect\.facebook\.net\/en_US\/all\.js/i,
        // Woopra flakiness
        /eatdifferent\.com\.woopra-ns\.com/i,
        /static\.woopra\.com\/js\/woopra\.js/i,
        // Chrome extensions
        /extensions\//i,
        /^chrome:\/\//i,
        /^chrome-extension:\/\//i,
        // Other plugins
        /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
        /webappstoolbarba\.texthelp\.com\//i,
        /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,

        // Our own custom url filters
        /cdn\.cookielaw\.org/i,
        /www\.googletagmanager\.com/i,
        /t\.contentsquare\.net/i,
        /live-chat-azure-prod18\.sprinklr\.com/i,
        /www\.clarity\.ms/i,
      ],
      ignoreErrors: [
        // Sentry recommendations: https://docs.sentry.io/platforms/javascript/legacy-sdk/tips/
        // Random plugins/extensions
        'top.GLOBALS',
        // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
        'originalCreateNotification',
        'canvas.contentDocument',
        'MyApp_RemoveAllHighlights',
        'http://tt.epicplay.com',
        "Can't find variable: ZiteReader",
        'jigsaw is not defined',
        'ComboSearch is not defined',
        'http://loading.retry.widdit.com/',
        'atomicFindClose',
        // Facebook borked
        'fb_xd_fragment',
        // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to reduce this. (thanks @acdha)
        // See http://stackoverflow.com/questions/4113268/how-to-stop-javascript-injection-from-vodafone-proxy
        'bmi_SafeAddOnload',
        'EBCallBackMessageReceived',
        // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
        'conduitPage',
        // Generic error code from errors outside the security sandbox
        // You can delete this if using raven.js > 1.0, which ignores these automatically.
        'Script error.',
        // Avast extension error
        '_avast_submit',

        // Our own custom error messages to ignore
        'Unknown Error',
        'Load failed',
        'IFrameMessageHandler',
        'The object can not be cloned',
        'Error invoking post',
        'Importing a module script failed',
      ],
      beforeSend: event => {
        if (event.exception?.values?.[0]?.stacktrace?.frames?.some(({ filename }) => filename?.includes('gtm.js'))) {
          return null;
        }
        return event;
      },
    });
    setTag('brand', this.brand);
    setTag('app', this.appId);
  }

  public captureException(error: CustomError): void {
    if (isDevMode()) {
      console.warn('[SentryService] Capturing error:', error);
    }

    const normalizedError = this.normalizeError(error);

    this.addBreadcrumbForError(error);

    try {
      captureException(normalizedError);
    } catch (e) {
      console.warn('[SentryService] Failed to capture exception:', e);
    }
  }

  private addBreadcrumbForError(error: CustomError): void {
    const now = Date.now() / 1000;

    try {
      if (typeof error === 'string') {
        addBreadcrumb({
          category: 'string-error',
          message: error,
          level: 'error',
          timestamp: now,
        });
      } else if (error instanceof HttpErrorResponse) {
        addBreadcrumb({
          category: 'http',
          message: `[HTTP ${error.status}] ${error.message}`,
          level: 'error',
          data: {
            url: error.url,
            status: error.status,
            error: this.safeStringify(error.error),
          },
          timestamp: now,
        });
      } else if (error instanceof Error) {
        addBreadcrumb({
          category: 'js',
          message: error.message,
          level: 'error',
          data: {
            name: error.name,
            stack: error.stack,
          },
          timestamp: now,
        });
      } else if (typeof error === 'object' && error !== null) {
        addBreadcrumb({
          category: 'unknown-object',
          message: 'Object captured as exception',
          level: 'error',
          data: {
            keys: Object.keys(error),
            json: this.safeStringify(error),
          },
          timestamp: now,
        });
      } else {
        addBreadcrumb({
          category: 'unknown',
          message: 'Unknown error type captured',
          level: 'error',
          data: {
            raw: String(error),
          },
          timestamp: now,
        });
      }
    } catch (breadcrumbError) {
      if (isDevMode()) {
        console.warn('[SentryService] Failed to add breadcrumb:', breadcrumbError);
      }
    }
  }

  private normalizeError(error: CustomError): Error {
    if (typeof error === 'string') {
      return new Error(error);
    }

    if (error instanceof Error) {
      return error;
    }

    if (error instanceof HttpErrorResponse) {
      if (typeof error.error?.error === 'string') {
        return new Error(`[MS] ${error.error.error}`, { cause: error });
      }
      return new Error(`[HttpError] ${error.message}`, { cause: error });
    }

    return new Error('Unknown error captured', { cause: error as object });
  }

  private safeStringify(obj: unknown): string {
    try {
      return JSON.stringify(obj);
    } catch {
      return '[Unserializable Object]';
    }
  }

  private getEnvironment() {
    const origin = this.browserService.getOrigin();
    if (origin.includes('localhost')) return 'local';
    if (origin.includes('.dev.')) return 'development';
    if (origin.includes('prodpreview.')) return 'prodpreview';
    return 'production';
  }
}
